/**
 * Fachliches Kernmodell: ein Drumpattern ueber einen Takt (4/4, Sechzehntel).
 *
 * Die Klasse kapselt ihren Zustand vollstaendig. Von aussen ist keine
 * direkte Referenz auf die interne Datenstruktur erreichbar; Aenderungen
 * erfolgen ausschliesslich ueber die definierten Methoden.
 *
 * Diese Datei haengt nicht von HTML, DOM oder UI ab.
 */

import {
  DrumInstrument,
  HitType,
  INSTRUMENT_ORDER,
  STEP_COUNT,
  isDrumInstrument,
  isHitType,
  isStepIndex,
  type DrumHit,
  type StepIndex,
  type Sticking,
  isSticking,
  isSubdivision,
  stepsForBeat,
  type Subdivision,
} from './types';
import {
  PATTERN_FORMAT_VERSION,
  PATTERN_META,
  type SerializedHit,
  type SerializedPattern,
} from './serialization';

/**
 * Fehler, der bei ungueltigen Eingaben oder beschaedigten
 * gespeicherten Daten geworfen wird.
 */
export class PatternError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PatternError';
  }
}

/** Interner Schluessel fuer die Ablage eines Schlags. */
function cellKey(instrument: DrumInstrument, step: StepIndex): string {
  return `${instrument}:${Math.round(step * 3)}`;
}

function assertInstrument(value: unknown): asserts value is DrumInstrument {
  if (!isDrumInstrument(value)) {
    throw new PatternError(`Unbekanntes Instrument: ${String(value)}`);
  }
}

function assertStep(value: unknown): asserts value is StepIndex {
  if (!isStepIndex(value)) {
    throw new PatternError(
      `Ungueltiger Schrittindex: ${String(value)} (erlaubt: 0..${STEP_COUNT - 1})`,
    );
  }
}

export class DrumPattern {
  /**
   * Pro Zelle (Instrument + Schritt) hoechstens ein Schlag.
   * Die Map ist privat und wird niemals nach aussen gereicht.
   */
  readonly #hits = new Map<string, DrumHit>();
  readonly #sticking: Sticking[] = Array.from(
    { length: STEP_COUNT * 3 },
    () => '',
  );

  readonly #subdivisions: Subdivision[] = [4, 4, 4, 4];

  get subdivisions(): Subdivision[] {
    return [...this.#subdivisions];
  }
  get steps(): number[] {
    return this.#subdivisions.flatMap((subdivision, beat) =>
      stepsForBeat(beat, subdivision),
    );
  }
  setSubdivision(beat: number | null, subdivision: Subdivision): void {
    if (
      !isSubdivision(subdivision) ||
      (beat !== null && (!Number.isInteger(beat) || beat < 0 || beat > 3))
    ) {
      throw new PatternError('Ungueltige Rasterauswahl.');
    }
    const beats = beat === null ? [0, 1, 2, 3] : [beat];
    for (const index of beats) {
      const allowed = new Set(
        stepsForBeat(index, subdivision).map((step) => Math.round(step * 3)),
      );
      for (let tick = index * 12; tick < (index + 1) * 12; tick++) {
        if (allowed.has(tick)) continue;
        if (
          this.#sticking[tick] ||
          INSTRUMENT_ORDER.some((instrument) =>
            this.#hits.has(cellKey(instrument, tick / 3)),
          )
        ) {
          throw new PatternError(
            'Rasterwechsel nicht möglich: Erst Schläge und Handsatz auf nicht passenden Positionen entfernen.',
          );
        }
      }
    }
    for (const index of beats) this.#subdivisions[index] = subdivision;
  }
  #assertActiveStep(step: number): void {
    assertStep(step);
    const beat = Math.floor(step / 4);
    if (!stepsForBeat(beat, this.#subdivisions[beat]!).includes(step)) {
      throw new PatternError('Position liegt nicht im gewählten Raster.');
    }
  }

  getSticking(step: StepIndex): Sticking {
    this.#assertActiveStep(step);
    return this.#sticking[Math.round(step * 3)]!;
  }

  setSticking(step: StepIndex, value: Sticking): void {
    this.#assertActiveStep(step);
    if (!isSticking(value)) throw new PatternError('Ungueltiger Handsatz.');
    this.#sticking[Math.round(step * 3)] = value;
  }

  /** Erzeugt ein leeres Pattern ohne Schlaege. */
  static createEmpty(): DrumPattern {
    return new DrumPattern();
  }

  /**
   * Setzt einen Schlag fuer Instrument und Schritt.
   * Ein bereits vorhandener Schlag wird ueberschrieben, sodass pro
   * Zelle stets hoechstens ein Schlag existiert.
   */
  setHit(
    instrument: DrumInstrument,
    step: StepIndex,
    type: HitType = HitType.Normal,
  ): void {
    assertInstrument(instrument);
    this.#assertActiveStep(step);
    if (!isHitType(type)) {
      throw new PatternError(`Unbekannte HitType: ${String(type)}`);
    }
    this.#hits.set(cellKey(instrument, step), { type });
  }

  /** Entfernt einen Schlag. Ist die Zelle leer, bleibt sie leer. */
  removeHit(instrument: DrumInstrument, step: StepIndex): void {
    assertInstrument(instrument);
    this.#assertActiveStep(step);
    this.#hits.delete(cellKey(instrument, step));
  }

  /**
   * Schaltet eine Zelle um:
   * leer -> normaler Schlag, vorhandener Schlag -> leer.
   * Gibt zurueck, ob nach dem Umschalten ein Schlag vorhanden ist.
   */
  toggleHit(instrument: DrumInstrument, step: StepIndex): boolean {
    assertInstrument(instrument);
    this.#assertActiveStep(step);
    if (this.hasHit(instrument, step)) {
      this.removeHit(instrument, step);
      return false;
    }
    this.setHit(instrument, step);
    return true;
  }

  /** Prueft, ob an einer Zelle ein Schlag vorhanden ist. */
  hasHit(instrument: DrumInstrument, step: StepIndex): boolean {
    assertInstrument(instrument);
    this.#assertActiveStep(step);
    return this.#hits.has(cellKey(instrument, step));
  }

  /**
   * Liefert eine Kopie des Schlags an einer Zelle oder `undefined`.
   * Die Rueckgabe ist eine Kopie, damit der interne Zustand nicht
   * von aussen veraendert werden kann.
   */
  getHit(instrument: DrumInstrument, step: StepIndex): DrumHit | undefined {
    assertInstrument(instrument);
    this.#assertActiveStep(step);
    const hit = this.#hits.get(cellKey(instrument, step));
    return hit ? { type: hit.type } : undefined;
  }

  /** Anzahl gesetzter Schlaege. */
  get hitCount(): number {
    return this.#hits.size;
  }

  /** Entfernt alle Schlaege. */
  clear(): void {
    this.#hits.clear();
    this.#sticking.fill('');
    this.#subdivisions.fill(4);
  }

  /**
   * Ueberfuehrt das Pattern in eine flache, JSON-taugliche Struktur.
   * Die Schlaege werden in stabiler Reihenfolge (Instrument, Schritt)
   * ausgegeben, damit Serialisierungen deterministisch sind.
   */
  toSerializable(): SerializedPattern {
    const hits: SerializedHit[] = [];
    for (const instrument of INSTRUMENT_ORDER) {
      for (const step of this.steps) {
        const hit = this.#hits.get(cellKey(instrument, step));
        if (hit) {
          hits.push({ instrument, step: Math.round(step * 3), type: hit.type });
        }
      }
    }
    return {
      version: PATTERN_FORMAT_VERSION,
      bars: PATTERN_META.bars,
      timeSignature: {
        numerator: PATTERN_META.timeSignature.numerator,
        denominator: PATTERN_META.timeSignature.denominator,
      },
      stepCount: STEP_COUNT * 3,
      hits,
      sticking: [...this.#sticking],
      subdivisions: this.subdivisions,
    };
  }

  /**
   * Stellt ein Pattern aus einer serialisierten Struktur wieder her.
   * Ungueltige oder unvollstaendige Daten werden mit einem
   * {@link PatternError} abgelehnt.
   */
  static fromSerializable(data: unknown): DrumPattern {
    if (typeof data !== 'object' || data === null) {
      throw new PatternError('Serialisierte Daten muessen ein Objekt sein.');
    }
    const record = data as Record<string, unknown>;

    if (record.version !== 1 && record.version !== PATTERN_FORMAT_VERSION) {
      throw new PatternError(
        `Nicht unterstuetzte Formatversion: ${String(record.version)}`,
      );
    }
    if (record.stepCount !== STEP_COUNT * (record.version === 2 ? 3 : 1)) {
      throw new PatternError(
        `Unerwartete Schrittanzahl: ${String(record.stepCount)}`,
      );
    }
    const meter = record.timeSignature as Record<string, unknown> | null;
    if (
      record.bars !== 1 ||
      typeof meter !== 'object' ||
      meter === null ||
      meter.numerator !== 4 ||
      meter.denominator !== 4
    ) {
      throw new PatternError('Es wird ein Takt in 4/4 erwartet.');
    }
    if (!Array.isArray(record.hits)) {
      throw new PatternError('Feld "hits" muss ein Array sein.');
    }

    const pattern = new DrumPattern();
    if (record.version === 2) {
      if (
        !Array.isArray(record.subdivisions) ||
        record.subdivisions.length !== 4 ||
        !record.subdivisions.every(isSubdivision)
      ) {
        throw new PatternError('Ungueltige Unterteilungen.');
      }
      record.subdivisions.forEach((subdivision, beat) =>
        pattern.setSubdivision(beat, subdivision),
      );
    }
    for (const raw of record.hits as unknown[]) {
      if (typeof raw !== 'object' || raw === null) {
        throw new PatternError('Ein Schlag muss ein Objekt sein.');
      }
      const hit = raw as Record<string, unknown>;
      if (!isDrumInstrument(hit.instrument)) {
        throw new PatternError(
          `Unbekanntes Instrument: ${String(hit.instrument)}`,
        );
      }
      const step =
        typeof hit.step === 'number'
          ? hit.step / (record.version === 2 ? 3 : 1)
          : NaN;
      if (!Number.isInteger(hit.step) || !isStepIndex(step)) {
        throw new PatternError(`Ungueltiger Schrittindex: ${String(hit.step)}`);
      }
      if (!isHitType(hit.type)) {
        throw new PatternError(`Unbekannte HitType: ${String(hit.type)}`);
      }
      pattern.#assertActiveStep(step);
      const key = cellKey(hit.instrument, step);
      if (pattern.#hits.has(key)) {
        throw new PatternError(
          `Doppelter Schlag fuer ${hit.instrument} an Schritt ${String(hit.step)}.`,
        );
      }
      pattern.#hits.set(key, { type: hit.type });
    }
    if (record.sticking !== undefined) {
      if (
        !Array.isArray(record.sticking) ||
        record.sticking.length !== STEP_COUNT * (record.version === 2 ? 3 : 1)
      ) {
        throw new PatternError('Ungueltige Anzahl Handsatzpositionen.');
      }
      for (let index = 0; index < record.sticking.length; index++) {
        const value: unknown = record.sticking[index];
        if (!isSticking(value)) throw new PatternError('Ungueltiger Handsatz.');
        if (value !== '')
          pattern.setSticking(index / (record.version === 2 ? 3 : 1), value);
      }
    }
    return pattern;
  }
}
